import { AppData } from "../../types/app";

const app: AppData = {
  slug: "limelink",
  name: "LimeLink",
  summary: "앱 설치 전후로 원하는 화면에 보내 주는 딥링크 서비스",
  category: "개발 도구",
  platforms: ["web"],
  status: "live",
  released_at: "2026-08-03",
  pinned: true,
  metric: "글로벌 가입자 1,000명",
  icon: "/apps/limelink/icon.png",
  links: { web: "https://limelink.org" },
  has_account: true,
  operator: { name: "Hellovelop Inc.", email: "limelink2024@gmail.com" },
  role: "Hellovelop 취미 팀의 백엔드 리드. 서비스 재설계와 이관 계획, 기술 결정을 맡았다.",
  why:
    "짧은 링크 하나로 앱이 있으면 앱 화면으로, 없으면 스토어로 보내고, 설치한 뒤에도 원래 보려던 화면으로 이어 주는 딥링크 서비스다. Firebase Dynamic Links 같은 기능이 필요한 앱 팀이 직접 서버를 두지 않고 쓸 수 있게 만들었다. 운영 중인 첫 버전을 웹, iOS, Android 연동과 결제를 끊지 않고 새 구조로 옮겼다. 글로벌 가입자는 1,000명이다.",
  features: [
    { title: "플랫폼별 이동", body: "링크를 연 기기에 맞춰 앱 화면이나 스토어로 보낸다. 랜딩 조회, 앱 열기, 스토어 이동, 설치 전환을 집계해 보여 준다." },
    { title: "디퍼드 딥링크", body: "앱을 설치하기 전에 누른 링크를 설치 후 첫 실행까지 이어 준다." },
    { title: "Universal Link, App Links 자동 구성", body: "프로젝트에 앱 정보를 등록하면 iOS와 Android 앱 연결에 필요한 파일을 대신 만들어 준다." },
    { title: "Custom Domain", body: "Pro 프로젝트는 자기 도메인으로 링크를 만든다. DNS와 인증서 상태는 자동으로 확인한다." },
    { title: "조직과 팀", body: "조직과 프로젝트 단위로 멤버를 초대하고 역할을 나눈다. API 키로 서버에서 링크를 만들 수 있다." },
  ],
  screenshots: [{ src: "/apps/limelink/logo.png", alt: "LimeLink 로고" }],
  policy: {
    privacy: {
      effective_at: "2026-11-01",
      sections: [
        {
          id: "purpose",
          title: "개인정보의 처리 목적",
          body: [
            "LimeLink(이하 \"서비스\")는 다음 목적으로 개인정보를 처리합니다. 목적이 바뀌면 「개인정보 보호법」 제18조에 따라 별도 동의를 받습니다.",
            "회원 가입과 로그인, 본인 확인, 조직과 프로젝트 관리, 멤버 초대 메일 발송.",
            "링크를 누른 사람을 플랫폼에 맞는 앱 화면이나 스토어로 보내고, 앱 설치 후 원래 화면으로 이어 주는 디퍼드 딥링크 매칭.",
            "유료 구독 결제, 청구서 발급, 결제 이메일 인증.",
            "링크 이용 통계 집계(개인을 식별할 수 없는 숫자로만 집계), 부정 이용 방지와 보안.",
          ],
        },
        {
          id: "items",
          title: "처리하는 개인정보 항목",
          body: [
            "서비스는 Google 계정 로그인만 지원하며 비밀번호를 받지 않습니다. 결제 수단 정보는 Paddle 결제 화면에서 Paddle이 직접 받으며 서비스 서버에는 저장하지 않습니다.",
            "관리 콘솔은 로그인 유지를 위해 limelink_session(HttpOnly, Secure, 14일), limelink_session_present(14일) 쿠키를 씁니다. 링크 랜딩 페이지는 쿠키를 쓰지 않습니다. 서비스는 외부 광고, 분석 도구를 쓰지 않습니다.",
            "쿠키 저장은 브라우저 설정에서 거부하거나 삭제할 수 있습니다. 다만 거부하면 관리 콘솔에 로그인한 상태를 유지할 수 없습니다.",
          ],
          table: {
            head: ["구분", "항목", "수집 시점"],
            rows: [
              ["회원(필수)", "이메일, Google 계정 식별자, 이메일 인증 여부, 이름, 프로필 사진 URL", "Google 로그인으로 가입하거나 로그인할 때"],
              ["회원(선택)", "마케팅 수신 동의 여부", "가입하거나 설정을 바꿀 때"],
              ["회원(자동)", "로그인 세션 토큰 해시, 세션 만료와 폐기 시각", "로그인할 때"],
              ["조직", "조직명, 멤버 역할, 초대받는 사람의 이메일", "조직을 만들거나 멤버를 초대할 때"],
              ["결제", "청구 이메일, 청구 이름, 주소와 사업자 정보(입력한 경우), Paddle 고객 ID, 결제 금액과 통화, Paddle 거래와 구독 ID, 결제 알림 원문", "유료 구독을 시작하거나 결제가 일어날 때"],
              ["업로드", "로고 등 회원이 올린 이미지", "이미지를 올릴 때"],
              ["링크 클릭자(비회원)", "IP 주소, User-Agent, 화면 크기, 요청 URL", "모바일에서 링크를 열 때"],
              ["링크 클릭자(iOS)", "IP 주소와 IP 대역, User-Agent, 화면 크기와 배율, 언어, 타임존, 터치 포인트 수, OS 주 버전, 원본 URL", "랜딩 페이지에서 App Store로 이동하기 전"],
              ["링크 클릭자(Android)", "IP 주소, User-Agent, 화면 정보", "랜딩 페이지에서 기기 확인을 요청할 때"],
              ["서비스 이용 기록", "서버 접속 로그(IP, User-Agent, 쿠키, 인증 헤더는 가린 상태로 기록)", "서비스를 이용할 때"],
            ],
          },
        },
        {
          id: "retention",
          title: "개인정보의 보유와 이용 기간",
          body: [
            "회원 정보는 탈퇴할 때까지 보유하고, 탈퇴하면 지체 없이 파기합니다. 탈퇴한 회원이 유일한 소유자였던 조직, 프로젝트, 링크, 업로드 이미지는 탈퇴 후 30일 안에 영구 삭제합니다.",
            "iOS 매칭용 브라우저 신호와 Android 매칭 코드는 수집 후 24시간이 지나면 1시간 주기 작업으로 삭제합니다.",
            "비회원 링크 클릭 기록(IP 주소, User-Agent, 화면 크기, 요청 URL)과 Android 기기 확인 정보는 수집 후 30일 안에 삭제합니다.",
            "가입 대기 정보와 계정 연결 확인 정보는 10분 동안만 유효하며, 만료 후 24시간 안에 삭제합니다.",
            "서버 접속 로그는 90일 동안 보관한 뒤 자동 삭제합니다.",
            "관계 법령에 따라 보존해야 하는 정보는 그 기간 동안 따로 보관합니다. 「전자상거래 등에서의 소비자보호에 관한 법률」에 따라 계약 또는 청약철회 기록과 대금결제 및 재화 등의 공급 기록은 5년, 소비자 불만 또는 분쟁 처리 기록은 3년 보관합니다.",
          ],
        },
        {
          id: "third-party",
          title: "개인정보의 제3자 제공",
          body: [
            "서비스는 정보주체의 개인정보를 제3자에게 제공하지 않습니다. 법령에 특별한 규정이 있거나 수사기관이 법령에 정한 절차에 따라 요구하는 경우는 예외입니다.",
            "프로젝트 소유자(회원)는 자기 링크의 일, 시간 단위 집계 숫자만 볼 수 있으며, 개별 클릭자의 IP 주소나 User-Agent는 받지 않습니다. 회원의 앱이 디퍼드 딥링크를 조회하면 매칭된 목적지 URI와 원본 링크 URL만 전달합니다.",
          ],
        },
        {
          id: "outsourcing",
          title: "개인정보 처리 위탁과 국외 이전",
          body: [
            "서비스는 아래 업체에 개인정보 처리를 위탁하며, 일부는 국외로 이전됩니다.",
            "이전 시기와 방법: 서비스를 이용할 때 암호화된 네트워크(HTTPS)로 전송합니다.",
            "국외 이전을 원하지 않으면 회원 탈퇴로 거부할 수 있습니다. 다만 Google 로그인과 결제는 서비스 이용에 필요하므로, 거부하면 서비스를 이용할 수 없습니다.",
          ],
          table: {
            head: ["업체", "위탁 업무", "이전 국가", "이전 항목", "연락처", "보유 기간"],
            rows: [
              ["Amazon Web Services", "서버 운영, 데이터와 파일 저장, 메일 발송, 로그 보관", "대한민국(서울 리전)", "서비스가 처리하는 개인정보 전체, 초대와 결제 인증 메일 수신자 이메일", "https://aws.amazon.com/privacy/", "회원 탈퇴 또는 위탁 계약 종료 시까지"],
              ["Google LLC", "회원 로그인 인증", "미국", "Google 로그인 요청, Google 계정 식별 정보", "https://policies.google.com/privacy", "회원 탈퇴 또는 위탁 계약 종료 시까지"],
              ["Paddle.com Market Limited", "구독 결제 대행, 청구서 발급", "영국", "청구 이메일, 이름, 주소, 사업자 정보, 결제 수단 정보", "https://www.paddle.com/legal/privacy", "회원 탈퇴 또는 위탁 계약 종료 시까지"],
              ["Cloudflare, Inc.", "회원 맞춤 도메인 연결과 인증서 발급", "미국", "회원의 도메인 이름, 해당 도메인 링크 클릭자의 IP 주소, User-Agent, 요청 URL", "https://www.cloudflare.com/privacypolicy/", "회원 탈퇴 또는 위탁 계약 종료 시까지"],
              ["jsDelivr", "랜딩 페이지 글꼴 전송", "업체 고지 국가(문의 시 안내)", "랜딩 페이지 방문자의 IP 주소", "https://www.jsdelivr.com", "위탁 계약 종료 시까지"],
            ],
          },
        },
        {
          id: "destruction",
          title: "개인정보의 파기 절차와 방법",
          body: [
            "보유 기간이 끝나거나 처리 목적을 이룬 개인정보는 지체 없이 파기합니다. 법령에 따라 보존해야 하는 정보는 다른 데이터와 분리해 보관하고, 보존 기간이 끝나면 파기합니다.",
            "전자 파일은 복구할 수 없는 방법으로 데이터베이스와 저장소에서 삭제합니다. 만료 기한이 정해진 정보는 주기 작업이 자동으로 삭제합니다.",
          ],
        },
        {
          id: "rights",
          title: "정보주체의 권리와 행사 방법",
          body: [
            "정보주체는 언제든지 개인정보 열람, 정정, 삭제, 처리 정지를 요구할 수 있습니다. limelink2024@gmail.com으로 요청하면 영업일 기준 3일 안에 답변하고 지체 없이 조치합니다. 법정대리인이나 위임받은 사람도 대신 요청할 수 있습니다.",
            "회원은 위 이메일로 계정 삭제를 요청할 수 있습니다. 마케팅 수신 동의는 설정에서 언제든지 철회할 수 있습니다.",
            "회원이 아닌 링크 클릭자도 위 이메일로 요구할 수 있습니다. 다만 매칭용 정보는 24시간 안에 삭제되므로 그 뒤에는 남아 있지 않을 수 있습니다.",
          ],
        },
        {
          id: "security",
          title: "개인정보의 안전성 확보 조치",
          body: [
            "모든 통신은 HTTPS로 암호화하며, 데이터베이스 연결도 TLS로 암호화합니다.",
            "로그인 세션 토큰과 API 키는 SHA-256 해시로만 저장하고, 세션 쿠키는 HttpOnly, Secure 속성으로 발급합니다.",
            "조직과 프로젝트 단위로 접근을 분리해 다른 회원의 데이터에 접근할 수 없게 하고, 결제 알림은 서명을 검증한 뒤 처리합니다.",
            "서버 로그에서 IP 주소, User-Agent, 쿠키, 인증 헤더, API 키를 가리고, 비밀값은 AWS SSM Parameter Store에 따로 보관합니다.",
          ],
        },
        {
          id: "officer",
          title: "개인정보 보호책임자",
          body: [
            "개인정보 보호책임자: 이광복, limelink2024@gmail.com",
            "개인정보 침해에 대한 신고나 상담은 개인정보분쟁조정위원회(1833-6972), 개인정보침해신고센터(118), 대검찰청(1301), 경찰청(182)에도 할 수 있습니다.",
          ],
        },
        {
          id: "changes",
          title: "개인정보 처리방침의 변경",
          body: [
            "이 방침은 2026년 11월 1일부터 시행합니다.",
            "내용을 바꾸면 시행 7일 전에 이 페이지에 공지하고, 이용자에게 불리하거나 중요한 변경은 30일 전에 공지합니다.",
          ],
        },
      ],
    },
    terms: {
      effective_at: "2026-11-01",
      sections: [
        {
          id: "purpose",
          title: "목적",
          body: ["이 약관은 Hellovelop Inc.(이하 \"운영자\")이 제공하는 LimeLink(이하 \"서비스\")의 이용 조건과 절차, 운영자와 회원의 권리, 의무를 정합니다."],
        },
        {
          id: "definitions",
          title: "정의",
          body: [
            "\"회원\"은 이 약관에 동의하고 Google 계정으로 가입한 사람입니다.",
            "\"조직\"과 \"프로젝트\"는 회원이 링크, 앱, 도메인, 멤버를 관리하는 단위입니다.",
            "\"링크\"는 서비스가 만든 짧은 URL이며, \"링크 클릭자\"는 링크를 연 사람입니다.",
            "\"Pro 구독\"은 프로젝트 단위로 결제하는 유료 요금제입니다.",
          ],
        },
        {
          id: "effect",
          title: "약관의 효력과 변경",
          body: [
            "이 약관은 서비스 화면에 게시하고 회원이 가입할 때 동의하면 효력이 생깁니다.",
            "운영자는 관련 법령을 어기지 않는 범위에서 약관을 바꿀 수 있습니다. 바꾸면 시행 7일 전에 공지하고, 이용자에게 불리하거나 중요한 변경은 30일 전에 공지합니다. 회원이 변경에 동의하지 않으면 탈퇴할 수 있습니다.",
          ],
        },
        {
          id: "service",
          title: "서비스의 제공과 변경",
          body: [
            "서비스는 동적 링크 생성과 플랫폼별 리다이렉트, 디퍼드 딥링크, Universal Link와 App Links 파일 생성, 링크 통계, 조직과 API 키 관리, Pro 프로젝트의 Custom Domain을 제공합니다.",
            "iOS 디퍼드 딥링크는 브라우저 신호를 점수화하는 방식이라 매칭이 실패할 수 있으며, 운영자는 매칭 결과를 보장하지 않습니다.",
            "운영자는 운영상, 기술상 필요에 따라 서비스 내용을 바꿀 수 있고, 회원에게 영향이 큰 변경은 미리 공지합니다.",
          ],
        },
        {
          id: "account",
          title: "계정",
          body: [
            "회원은 Google 계정으로 가입하고 로그인합니다. 회원은 자기 Google 계정과 발급받은 API 키를 안전하게 관리해야 합니다.",
            "회원은 언제든지 탈퇴할 수 있습니다. 탈퇴하면 계정 정보는 바로 삭제하고, 회원이 유일한 소유자인 조직과 프로젝트는 개인정보 처리방침에 정한 기간 안에 삭제합니다.",
          ],
        },
        {
          id: "billing",
          title: "유료 서비스와 환불",
          body: [
            "Pro 구독은 프로젝트마다 월 USD 10 또는 연 USD 100이며, 결제와 청구서 발급은 Paddle이 대행합니다. 월간 구독은 연간으로 바꿀 수 있고, 연간에서 월간으로는 바꿀 수 없습니다.",
            "구독 해지를 예약하면 이미 결제한 기간이 끝날 때까지 Pro 기능을 쓸 수 있고 다음 결제는 청구되지 않습니다. 결제가 끝나도 마지막 유료 기간 종료 후 1년 동안 기존 Custom Domain 링크는 동작합니다.",
            "청약철회와 환불은 「전자상거래 등에서의 소비자보호에 관한 법률」에 따릅니다. 환불은 Paddle 결제 영수증의 안내 또는 limelink2024@gmail.com으로 요청할 수 있으며, 운영자는 영업일 기준 3일 안에 답변합니다.",
          ],
        },
        {
          id: "obligations",
          title: "이용자의 의무와 금지 행위",
          body: [
            "회원은 다음 행위를 해서는 안 됩니다. 피싱, 악성코드 배포, 사기 등 불법 목적의 링크를 만드는 행위. 다른 사람의 상표나 도메인을 권한 없이 쓰는 행위. 링크 클릭자의 개인정보를 부정하게 수집하는 행위. 서비스에 과도한 부하를 주거나 보안 장치를 우회하는 행위. 다른 회원의 조직이나 프로젝트에 권한 없이 접근하는 행위.",
            "회원은 자기 앱과 도메인을 서비스에 연결할 권한이 있어야 합니다.",
          ],
        },
        {
          id: "content",
          title: "콘텐츠의 권리",
          body: [
            "회원이 등록한 링크, 목적지 URL, 이미지, 스킨의 권리는 회원에게 있습니다. 운영자는 서비스 제공에 필요한 범위에서만 이를 저장하고 표시합니다.",
            "서비스 자체의 소프트웨어, 디자인, 상표에 대한 권리는 운영자에게 있습니다.",
          ],
        },
        {
          id: "suspension",
          title: "서비스 중단과 이용 제한",
          body: [
            "운영자는 설비 점검, 장애, 외부 서비스(AWS, Google, Paddle, Cloudflare) 장애 등으로 서비스를 일시 중단할 수 있으며, 가능하면 미리 공지합니다.",
            "회원이 이용자의 의무와 금지 행위 조항을 어기면 운영자는 해당 링크를 비활성화하거나 계정 이용을 제한할 수 있고, 제한 사유를 회원에게 알립니다.",
          ],
        },
        {
          id: "liability",
          title: "책임의 제한",
          body: [
            "운영자는 천재지변, 외부 서비스 장애 등 운영자가 통제할 수 없는 사유로 생긴 손해에 책임을 지지 않습니다.",
            "운영자는 회원이 만든 링크의 목적지 내용과 회원의 귀책 사유로 생긴 손해에 책임을 지지 않습니다. 다만 운영자의 고의나 중대한 과실로 생긴 손해는 예외입니다.",
          ],
        },
        {
          id: "dispute",
          title: "분쟁 해결과 준거법",
          body: [
            "이 약관과 서비스 이용에는 대한민국 법을 적용합니다.",
            "서비스 이용으로 생긴 분쟁의 관할 법원은 「민사소송법」에 따른 법원으로 합니다.",
          ],
        },
      ],
    },
  },
  faq: [
    {
      q: "Firebase Dynamic Links 대신 쓸 수 있나요?",
      a: "짧은 링크, 플랫폼별 앱과 스토어 분기, 설치 후 원래 화면으로 보내는 디퍼드 딥링크, Universal Link와 App Links 파일 생성을 제공합니다.",
    },
    {
      q: "앱을 설치하지 않은 사용자도 설치 후 원래 화면으로 가나요?",
      a: "Android는 Play Store referrer 코드로 정확히 연결합니다. iOS는 클릭 후 2시간 안의 브라우저 신호를 점수화해 70점 이상일 때만 연결하므로 실패할 수 있습니다.",
    },
    {
      q: "내 도메인으로 링크를 만들 수 있나요?",
      a: "Pro 구독 프로젝트는 Custom Domain을 붙일 수 있습니다. DNS와 인증서가 활성화되면 링크가 동작하고, 7일 안에 활성화되지 않으면 정리됩니다. 구독이 끝나도 마지막 결제 기간 종료 후 1년 동안 기존 링크는 유지됩니다.",
    },
    {
      q: "요금은 얼마인가요?",
      a: "기본은 무료이고, Pro는 프로젝트당 월 USD 10 또는 연 USD 100입니다. 월간에서 연간으로만 바꿀 수 있습니다.",
    },
    {
      q: "링크를 누른 사람의 정보는 얼마나 보관하나요?",
      a: "iOS 매칭용 신호와 Android 매칭 코드는 24시간 뒤 삭제합니다. 프로젝트 소유자는 개별 클릭자 정보 없이 집계 숫자만 봅니다.",
    },
    {
      q: "서버에서 링크를 만들 수 있나요?",
      a: "스코프를 지정한 Organization API 키로 링크 생성, 조회 API를 호출할 수 있습니다. 공개 OpenAPI 문서는 /api/v2/openapi.json입니다.",
    },
  ],
  deletion: {
    in_app_steps: [
      "가입한 Google 계정 이메일로 limelink2024@gmail.com에 계정 삭제를 요청합니다. 영업일 기준 3일 안에 답변하고, 30일 안에 삭제를 마칩니다.",
    ],
    deleted: [
      { item: "이메일, Google 계정 식별 정보, 로그인 세션", when: "즉시" },
      { item: "조직 멤버십, 업로드 이미지, 스킨", when: "즉시" },
      { item: "본인이 유일한 소유자인 조직의 API 키", when: "즉시 폐기" },
      { item: "본인이 유일한 소유자인 조직, 프로젝트, 링크", when: "30일 안에 영구 삭제" },
    ],
    retained: [
      {
        item: "결제 기록(금액, 통화, Paddle 거래와 구독 ID, 결제 알림 원문)",
        period: "5년",
        basis: "「전자상거래 등에서의 소비자보호에 관한 법률」 대금결제 및 재화 등의 공급에 관한 기록",
      },
    ],
  },
};

export default app;
